'use client';

import React from 'react';
import { Filter, type LucideIcon } from 'lucide-react';
import { cn } from '@sharpr/ui';
import { getMarketsForSport, getMarketLongName } from '@sharpr/types';
import type { MarketCode } from '@sharpr/types';
import { isPitcherMarket } from '@/utils/playerTaxonomy';
import { useSport } from '../../contexts/SportContext';
import { FilterShell } from './filters/FilterShell';
import { FilterChip } from './filters/FilterChip';

interface MarketFilterLocalProps {
  selectedMarkets: Set<MarketCode>;
  onMarketsChange: (markets: Set<MarketCode>) => void;
  availableMarkets?: Set<MarketCode>;
  isCollapsed?: boolean;
  /** Controlled chevron expand state (lifted so the panel can lay collapsed
   *  Teams/Markets headers side-by-side). For MLB this drives the lead
   *  (pitcher) row; the batter row manages its own collapse state. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  className?: string;
}

function MarketChip({ market, isSelected, onToggle }: {
  market: MarketCode;
  isSelected: boolean;
  onToggle: (market: MarketCode) => void;
}) {
  return (
    <FilterChip
      isSelected={isSelected}
      onClick={() => onToggle(market)}
    >
      {getMarketLongName(market)}
    </FilterChip>
  );
}

interface MarketShellProps {
  label: string;
  icon: LucideIcon;
  markets: readonly MarketCode[];
  selectedMarkets: Set<MarketCode>;
  onMarketsChange: (markets: Set<MarketCode>) => void;
  /** Taxonomy membership for this row — wider than `markets` (the currently
   *  AVAILABLE subset). Counts and clear operate on selected ∩ scope so a
   *  selected market that drops out of the data mid-session (game went live,
   *  finished game cleaned up) still counts and stays clearable; clearing by
   *  `markets` alone would strand it: invisible, "0 selected", yet still
   *  filtering the player list. */
  scope: (m: MarketCode) => boolean;
  isCollapsed?: boolean;
  defaultExpanded?: boolean;
  /** Controlled chevron state — only the lead row wires this to the parent. */
  expanded?: boolean;
  onExpandedChange?: (next: boolean) => void;
  className?: string;
}

/** One collapsible markets row. Counts / clear / tooltip are scoped to this
 *  group's taxonomy so the pitcher and batter rows stay independent. */
function MarketShell({
  label, icon, markets, selectedMarkets, onMarketsChange, scope,
  isCollapsed = false, defaultExpanded = true, expanded, onExpandedChange, className,
}: MarketShellProps) {
  const selectedInScope = [...selectedMarkets].filter(scope);
  const selectedCount = selectedInScope.length;
  const noneSelected = selectedCount === 0;
  const allAvailableSelected =
    markets.length > 0 && selectedCount === markets.length && markets.every(m => selectedMarkets.has(m));
  const labelLower = label.toLowerCase();

  const handleToggle = (market: MarketCode) => {
    const next = new Set(selectedMarkets);
    if (next.has(market)) next.delete(market);
    else next.add(market);
    onMarketsChange(next);
  };

  // Clear this group's whole taxonomy from the selected set — leaves the other
  // group's selection intact while sweeping no-longer-available markets too.
  const handleClear = () => {
    onMarketsChange(new Set([...selectedMarkets].filter(m => !scope(m))));
  };

  const collapsedTooltip = noneSelected
    ? `All ${labelLower} shown`
    : allAvailableSelected
      ? `All ${labelLower}`
      : selectedInScope.map(getMarketLongName).join(', ');

  return (
    <FilterShell
      icon={icon}
      label={label}
      isCollapsed={isCollapsed}
      defaultExpanded={defaultExpanded}
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      collapsedTooltip={isCollapsed ? `Filter ${labelLower}: ${collapsedTooltip}` : collapsedTooltip}
      collapsedBadge={selectedCount > 0 ? selectedCount : undefined}
      onCollapsedClick={() => { if (!noneSelected) handleClear(); }}
      collapsedClickDisabled={noneSelected}
      onClearAll={noneSelected ? undefined : handleClear}
      summary={
        selectedCount > 0 && !allAvailableSelected ? (
          <span className="text-xs text-brand-600 dark:text-brand-400 font-medium">
            {selectedCount} selected
          </span>
        ) : null
      }
      className={className}
    >
      {markets.map(market => (
        <MarketChip
          key={market}
          market={market}
          isSelected={selectedMarkets.has(market)}
          onToggle={handleToggle}
        />
      ))}
    </FilterShell>
  );
}

export function MarketFilterLocal({
  selectedMarkets,
  onMarketsChange,
  availableMarkets: availableMarketsFromData,
  isCollapsed = false,
  expanded,
  onExpandedChange,
  className,
}: MarketFilterLocalProps) {
  const sport = useSport();
  const allMarkets = getMarketsForSport(sport);
  const availableMarkets = availableMarketsFromData
    ? allMarkets.filter(m => availableMarketsFromData.has(m))
    : allMarkets;

  // MLB: pitcher markets lead in their own row; batter markets get a separate
  // row collapsed by default. NBA: one flat row.
  // A row also renders when its taxonomy has SELECTED markets with none
  // available — that's the only surface to clear a selection whose data
  // disappeared mid-session.
  if (sport === 'mlb') {
    const isBatterMarket = (m: MarketCode) => !isPitcherMarket(m);
    const pitcherMarkets = availableMarkets.filter(isPitcherMarket);
    const batterMarkets = availableMarkets.filter(isBatterMarket);
    const selectedList = [...selectedMarkets];
    const pitcherPresent = pitcherMarkets.length > 0 || selectedList.some(isPitcherMarket);
    const batterPresent = batterMarkets.length > 0 || selectedList.some(isBatterMarket);
    return (
      <div className={cn('space-y-2', className)}>
        {pitcherPresent && (
          <MarketShell
            label="Pitcher Markets"
            icon={Filter}
            markets={pitcherMarkets}
            selectedMarkets={selectedMarkets}
            onMarketsChange={onMarketsChange}
            scope={isPitcherMarket}
            isCollapsed={isCollapsed}
            defaultExpanded
            expanded={expanded}
            onExpandedChange={onExpandedChange}
          />
        )}
        {batterPresent && (
          <MarketShell
            label="Batter Markets"
            icon={Filter}
            markets={batterMarkets}
            selectedMarkets={selectedMarkets}
            onMarketsChange={onMarketsChange}
            scope={isBatterMarket}
            isCollapsed={isCollapsed}
            defaultExpanded={false}
          />
        )}
      </div>
    );
  }

  return (
    <MarketShell
      label="Markets"
      icon={Filter}
      markets={availableMarkets}
      selectedMarkets={selectedMarkets}
      onMarketsChange={onMarketsChange}
      scope={() => true}
      isCollapsed={isCollapsed}
      defaultExpanded
      expanded={expanded}
      onExpandedChange={onExpandedChange}
      className={className}
    />
  );
}
